import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartOptions,
  ChartType,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import _ from 'lodash';

import {
  ChartDataset,
  ChartDatasetFilter,
  ChartDatasetFilterOption,
  ChartHorizontalLimitLine,
  ChartLabel,
} from 'src/app/core/entities/chart-dataset';

import annotationPlugin from 'chartjs-plugin-annotation';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css'],
})
export class ChartComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('chartCanvas') public chartCanvas: ElementRef;
  @Input() public chartDatasets: ChartDataset[] = [];
  @Input() public chartXLabels: ChartLabel[] = [];
  @Input() public chartDatasetFilter: ChartDatasetFilter;
  @Input() public chartHorizontalLimitLines: ChartHorizontalLimitLine[] = [];
  @Input() public hasInteractiveLegend = true;
  @Input() public showMeasuringPointsTotal = true;
  @Input() public chartType: ChartType = 'line';
  @Input() public loading = true;
  @Input() public isPhq8 = false;
  @Output() public toggledFeatureDisplay: EventEmitter<ChartDataset> =
    new EventEmitter<ChartDataset>();
  @Output()
  public toggledFilterDisplay: EventEmitter<ChartDatasetFilterOption> = new EventEmitter<ChartDatasetFilterOption>();
  private _ctx: CanvasRenderingContext2D;
  private _chartDatasets: ChartDataset[] = [];
  private _chartOptions: ChartOptions = {
    plugins: {
      annotation: {
        common: {
          drawTime: 'beforeDraw'
        },
        annotations:{}
      },
      legend: {
        display: false,
      },
      tooltip: {
        caretSize: 0,
        caretPadding: 15,
        bodySpacing: 4,
        padding: {
          top: 15,
          right: 20,
          bottom: 15,
          left: 20,
        },
        usePointStyle: true,
        titleAlign: 'left',
        bodyAlign: 'left',
        displayColors: false,
        callbacks: {
          title: (context) => {
            const dataset = this._chartDatasets[context[0].datasetIndex];
            return dataset.data[context[0].dataIndex].tooltip.title;
          },
          label: (context) => {
            const dataset = this._chartDatasets[context.datasetIndex];
            return dataset.data[context.dataIndex].tooltip.body;
          },
          footer: (context) => {
            const dataset = this._chartDatasets[context[0].datasetIndex];
            const dataPoint = dataset.data[context[0].dataIndex];
            if (dataPoint.filteredBy === null) {
              return [];
            }

            return this.chartDatasetFilter.options
              .filter(
                (item) =>
                  item.hideOnTooltip !== true &&
                  item.show &&
                  dataPoint.filteredBy.includes(item.value)
              )
              .map((item) => item.label);
          },
        },
        backgroundColor: function (context) {
          return context.tooltip.dataPoints[0].dataset.borderColor.toString();
        },
        titleFont: {
          size: 13,
          family: 'Montserrat',
          weight: 'light',
        },
        bodyFont: {
          size: 12,
          family: 'Montserrat',
          weight: 'bold',
        },
        footerFont: {
          size: 12,
          family: 'Montserrat',
          weight: 'normal',
        },
      },
    },
    scales: {
      x: {
        grid: {
          borderWidth: 2,
          drawTicks: false,
          borderDash: [3, 3],
          offset: false,
          color: (context) => {
            return context.tick && context.tick.label ? "rgba(215, 219, 230, 1)" : "rgba(255, 255, 255, 0)";
          }
        },
        ticks: {
          color: '#8893aa',
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          padding: 20,
          font: {
            size: 15,
            family: 'Lato',
          },
        },
      },
    },
  };
  private _nbDefaultSteps = 6;
  public chart: Chart;
  public legend: any[] = [];
  public hasNoData = false;
  public measuringPointsTotal = 0;
  public legends: {[key:number]:string} = {
    1:$localize`:@@patient_evaluationPHQ8_situationDescriptionContentLi1:0-4 : aucune dépression ou dépression minimale.`, 
    2:$localize`:@@patient_evaluationPHQ8_situationDescriptionContentLi4:15-19 : dépression modérément sévère`, 
    3:$localize`:@@patient_evaluationPHQ8_situationDescriptionContentLi2:5-9 : dépression légère`, 
    4:$localize`:@@patient_evaluationPHQ8_situationDescriptionContentLi5:20-24 : dépression sévère`, 
    5:$localize`:@@patient_evaluationPHQ8_situationDescriptionContentLi3:10-14 : dépression modérée`, 
  };

  constructor() {
    Chart.register(
      CategoryScale,
      LinearScale,
      LineController,
      LineElement,
      PointElement,
      BarController,
      BarElement,
      Legend,
      Title,
      Tooltip,
      annotationPlugin
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['chartDatasets'] && !changes['chartDatasets'].firstChange) ||
      (changes['chartXLabels'] && !changes['chartXLabels'].firstChange)
    ) {
      this.updateChart();
    }
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this._ctx = this.chartCanvas.nativeElement.getContext('2d');

    const datasets = this.getChartDatasets();

    this.initChart(datasets);
  }

  /**
   * Init chart
   * @param datasets data input
   */
  initChart(datasets: any[]): void {
    // Set options
    this.setOptions();
    if(this.isPhq8){this.setAnnotations()}
    this.chart = new Chart(this._ctx, {
      type: this.chartType,
      data: {
        labels: this.chartXLabels.map((item) => item.title),
        datasets: datasets,
      },
      plugins: [
        {
          id: 'chartHorizontalLine',
          beforeDraw: (chart, args, options) => {
            chart.ctx.save();
            if (this.chartHorizontalLimitLines != undefined) {
              const visibleDatasets = this.chartDatasets.filter(
                (item) => !item.hidden
              );

              for (let j = 0; j < this.chartHorizontalLimitLines.length; j++) {
                const chartHorizontalLimitLine =
                  this.chartHorizontalLimitLines[j];
                const assignedDatasets = visibleDatasets.filter((item) =>
                  Object.keys(
                    chartHorizontalLimitLine.assignToAxisIds
                  ).includes(item.axis ? item.axis.id : 'y')
                );

                if (visibleDatasets.length > assignedDatasets.length && this.hasInteractiveLegend) {
                  continue;
                }

                const assignedDataset = assignedDatasets[0];
                if (assignedDataset != undefined) {
                  const axisId = assignedDataset.axis
                    ? assignedDataset.axis.id
                    : 'y';

                  chart.ctx.setLineDash([6, 8]);

                  chart.ctx.beginPath();
                  chart.ctx.strokeStyle = chartHorizontalLimitLine.color;
                  chart.ctx.moveTo(
                    chart.chartArea.left,
                    chart.scales[axisId].getPixelForValue(
                      chartHorizontalLimitLine.assignToAxisIds[axisId]
                    )
                  );
                  chart.ctx.lineTo(
                    chart.chartArea.right,
                    chart.scales[axisId].getPixelForValue(
                      chartHorizontalLimitLine.assignToAxisIds[axisId]
                    )
                  );
                  chart.ctx.stroke();

                  chart.ctx.restore();

                  const textWidth = this._ctx.measureText(
                    chartHorizontalLimitLine.label
                  ).width;
                  chart.ctx.fillStyle = '#FFFFFF';
                  chart.ctx.fillRect(
                    chart.chartArea.width / 2 +
                      chart.chartArea.left -
                      textWidth / 2 -
                      10,
                    chart.scales[axisId].getPixelForValue(
                      chartHorizontalLimitLine.assignToAxisIds[axisId]
                    ) - 15,
                    textWidth + 20,
                    30
                  );

                  chart.ctx.textBaseline = 'middle';
                  chart.ctx.fillStyle = chartHorizontalLimitLine.color;
                  chart.ctx.fillText(
                    chartHorizontalLimitLine.label,
                    chart.chartArea.width / 2 +
                      chart.chartArea.left -
                      textWidth / 2,
                    chart.scales[axisId].getPixelForValue(
                      chartHorizontalLimitLine.assignToAxisIds[axisId]
                    )
                  );
                }
              }
            }
          },
        },
        {
          id: 'chartCallbacks',
          afterUpdate: (chart, args, options) => {
            const visibleDatasets = this.chartDatasets.filter(
              (item) => !item.hidden
            );
            const visibleAxisGrid = visibleDatasets.map((item) =>
              item.axis ? item.axis.id : 'y'
            )[0];
            this.measuringPointsTotal = 0;

            this.chartDatasets.forEach((dataset, i) => {
              const axisId = dataset.axis ? dataset.axis.id : 'y';

              // Set measuring points
              if (
                chart.data.datasets[i].data.filter((item) => item !== null)
                  .length > this.measuringPointsTotal
              ) {
                this.measuringPointsTotal = chart.data.datasets[i].data.filter(
                  (item) => item !== null
                ).length;
              }

              // Set horizontal grid colors
              let gridColor: string | string[] = 'rgba(255, 255, 255, 0)';
              if (visibleAxisGrid === axisId) {
                gridColor = 'rgba(215, 219, 230, 1)';

                if (this.chartHorizontalLimitLines !== undefined) {
                  const chartHorizontalLimitLine =
                    this.chartHorizontalLimitLines.find((item) =>
                      Object.keys(item.assignToAxisIds).includes(
                        visibleAxisGrid
                      )
                    );

                  if (chartHorizontalLimitLine !== undefined) {
                    const assignedDatasets = visibleDatasets.filter((item) =>
                      Object.keys(
                        chartHorizontalLimitLine.assignToAxisIds
                      ).includes(item.axis ? item.axis.id : 'y')
                    );

                    if (visibleDatasets.length <= assignedDatasets.length || (visibleDatasets.length > assignedDatasets.length && !this.hasInteractiveLegend)) {
                      gridColor = [
                        ...Array(chart.scales[axisId].ticks.length - 1).keys(),
                      ]
                        .map((item) => 'rgba(215, 219, 230, 1)')
                        .concat(['rgba(255, 255, 255, 0)']);
                    }
                  }
                }
              }

              this.chart.options.scales[axisId]['grid']['color'] = gridColor;
            });
            this.hasNoData = chart.data.labels.length === 0;
          },
        },
      ],
      options: this._chartOptions,
    });

    this.setAdditionalData();
  }

  /**
   * Updates chart
   */
  updateChart(): void {
    this.legend = [];
    this.hasNoData = false;

    Object.keys(this.chart.options.scales).forEach((key) => {
      const index = this.chartDatasets.findIndex(
        (item) =>
          (item.axis && item.axis.id == key) || (key == 'y' && !item.axis)
      );

      if (-1 < index) {
        this.chartDatasets[index].hidden = !(this.chart.options.scales[key]
          .display as boolean);
        this.toggledFeatureDisplay.emit(this.chartDatasets[index]);
      }
    });

    const datasets = this.getChartDatasets();

    // Update data
    this.chart.data.datasets = datasets;
    this.chart.data.labels = this.chartXLabels.map((item) => item.title);

    // Set options
    this.setOptions();
    this.chart.options = this._chartOptions;

    this.setAdditionalData();
  }

  /**
   * Sets additionalData
   */
  setAdditionalData(): void {
    // Filter data
    this.setFilter();

    this.chart.update();

    // Set legend
    this.setLegend();
  }

  /**
   * Sets legend of chart
   */
  setLegend(): void {
    this.legend = Chart.defaults.plugins.legend.labels.generateLabels(
      this.chart
    );
  }

  /**
   * Gets legend label
   * @param legend text
   * @returns string
   */
  getLegendLabel(legend: any): string {
    if (Array.isArray(legend.text)) {
      return legend.text.join(' ');
    }

    return legend.text;
  }

  /**
   * Sets filter
   */
  setFilter(): void {
    if (this.chartDatasetFilter !== undefined) {
      this.chartDatasetFilter.options
        .sort((a, b) => a.order - b.order)
        .map((item) => (item.show = item.show ?? true));

      // Filter dataset data
      this.updateChartDataWithDatasetFilter();
    }
  }

  /**
   * Sets options of scales
   */
  setOptions(): void {
    this.chartDatasets.forEach((chartDataset) => {
      this.setScale(chartDataset);
    });
  }

 /**
  * Gets chart datasets
  * @returns any
  */
  getChartDatasets(): any[] {
    let datasets = [];

    this._chartDatasets = _.cloneDeep(this.chartDatasets);
    
    this._chartDatasets.forEach((chartDataset, i) => {
      let dataset = {
        type: chartDataset.type,
        label: chartDataset.label,
        hidden: chartDataset.hidden ?? false,
        data: chartDataset.data.map((item) => item.value),
        yAxisID: chartDataset.axis ? chartDataset.axis.id : 'y',
        borderColor: chartDataset.color,
        borderWidth: (context) => {
          return this.chartType === 'line' ? 1.2 : 0;
        },
        pointRadius: (context) => {
          const chartDataPoint =
            this._chartDatasets[context.datasetIndex].data[context.dataIndex];

          if (chartDataPoint === undefined) {
            return 0;
          }

          if (this.chartDatasetFilter !== undefined) {
            let pointStyleOptions = this.chartDatasetFilter.options.filter(
              (item) =>
                item.show &&
                chartDataPoint['filteredBy'] != null &&
                chartDataPoint['filteredBy'].includes(item.value)
            );

            if (pointStyleOptions.length > 0) {
              let pointStyle =
                (pointStyleOptions.length == 1
                  ? pointStyleOptions[0].pointStyle
                  : this.chartDatasetFilter.pointStyle) ??
                chartDataset.pointStyle ??
                null;

              return pointStyle ? 3 : 0;
            }
          }

          return chartDataset.pointStyle ? 3 : 0;
        },
        pointHoverRadius: (context) => {
          const chartDataPoint =
            this._chartDatasets[context.datasetIndex].data[context.dataIndex];

          if (chartDataPoint === undefined) {
            return 0;
          }

          if (this.chartDatasetFilter !== undefined) {
            let pointStyleOptions = this.chartDatasetFilter.options.filter(
              (item) =>
                item.show &&
                chartDataPoint['filteredBy'] != null &&
                chartDataPoint['filteredBy'].includes(item.value)
            );

            if (pointStyleOptions.length > 0) {
              let pointStyle =
                (pointStyleOptions.length == 1
                  ? pointStyleOptions[0].pointStyle
                  : this.chartDatasetFilter.pointStyle) ??
                chartDataset.pointStyle ??
                null;

              return pointStyle ? 3 : 0;
            }
          }

          return chartDataset.pointStyle ? 3 : 0;
        },
        pointStyle: (context) => {
          const chartDataPoint =
            this._chartDatasets[context.datasetIndex].data[context.dataIndex];

          if (chartDataPoint === undefined) {
            return null;
          }

          if (this.chartDatasetFilter !== undefined) {
            let pointStyleOptions = this.chartDatasetFilter.options.filter(
              (item) =>
                item.show &&
                chartDataPoint['filteredBy'] != null &&
                chartDataPoint['filteredBy'].includes(item.value)
            );

            if (pointStyleOptions.length > 0) {
              return (
                (pointStyleOptions.length == 1
                  ? pointStyleOptions[0].pointStyle
                  : this.chartDatasetFilter.pointStyle) ??
                chartDataset.pointStyle ??
                null
              );
            }
          }

          return chartDataset.pointStyle ?? null;
        },
        pointBackgroundColor: (context) => {
          const chartDataPoint =
            this._chartDatasets[context.datasetIndex].data[context.dataIndex];

          if (chartDataPoint === undefined) {
            return '#FFFFFF';
          }

          if (this.chartDatasetFilter !== undefined) {
            let pointStyleOptions = this.chartDatasetFilter.options.filter(
              (item) =>
                item.show &&
                chartDataPoint['filteredBy'] != null &&
                chartDataPoint['filteredBy'].includes(item.value)
            );

            if (pointStyleOptions.length > 0) {
              let pointStyle =
                pointStyleOptions.length == 1
                  ? pointStyleOptions[0].pointStyle
                  : this.chartDatasetFilter.pointStyle;

              return pointStyle ? chartDataset.color : '#FFFFFF';
            }
          }

          return '#FFFFFF';
        },
        backgroundColor: chartDataset.color,
        hoverBackgroundColor: chartDataset.color,
        pointHoverBorderColor: chartDataset.color,
        pointHoverBackgroundColor: chartDataset.color,
        pointBorderColor: chartDataset.color,
        spanGaps: true,
        barThickness: 10,
        categoryPercentage: 1,
        barPercentage: 1,
      };

      if ('bar' === this.chartType) {
        dataset['clip'] = { left: 2, top: false, right: 2, bottom: 0 };
      }

      datasets.push(dataset);

      const axisId = chartDataset.axis ? chartDataset.axis.id : 'y';
      this._chartOptions.scales[axisId] = {
        type: chartDataset.axis ? chartDataset.axis.type : 'linear',
        display: this.displayDatasetAxis(axisId),
        suggestedMin: 0,
        title: {
          display: false,
        },
        grid: {
          display: axisId == 'y' ? true : false,
        },
        beginAtZero: chartDataset.axis ? chartDataset.axis.beginAtZero : true,
        position: chartDataset.axis ? chartDataset.axis.position : 'left',
        ticks: {
          color: this.hasMultipleDatasets(axisId)
            ? '#8893aa'
            : chartDataset.color,
          padding: 10,
          font: {
            size: 15,
            family: 'Lato',
          },
        },
      };
    });

    return datasets;
  }

  /**
   * Displays dataset axis
   * @param axisId axis id
   * @returns boolean
   */
  displayDatasetAxis(axisId: string = 'y'): boolean {
    const nbDisplayedDatasets = this.chartDatasets.filter((item) =>
      axisId == 'y'
        ? (item.axis === undefined || item.axis.id == axisId) && !item.hidden
        : item.axis !== undefined && item.axis.id == axisId && !item.hidden
    ).length;

    return nbDisplayedDatasets > 0;
  }

  /**
   * Has multiple datasets chart
   * @param axisId axis id
   * @returns boolean
   */
  hasMultipleDatasets(axisId: string = 'y'): boolean {
    const nbDatasets = this.chartDatasets.filter((item) =>
      axisId == 'y'
        ? item.axis === undefined || item.axis.id == axisId
        : item.axis !== undefined && item.axis.id == axisId
    ).length;

    return nbDatasets > 1 ? true : false;
  }

  /**
   * Sets scale
   * @param dataset data inpout
   */
  setScale(dataset: ChartDataset): void {
    const axisId = dataset.axis ? dataset.axis.id : 'y';
    const firstDataset = this.chartDatasets.filter((item) =>
      axisId == 'y'
        ? item.axis === undefined || item.axis.id == axisId
        : item.axis !== undefined && item.axis.id == axisId
    )[0];

    this._chartOptions.scales[axisId]['title'] = {
      display: true,
      align: 'start',
      text:
        this.hasMultipleDatasets(axisId) && firstDataset.axis
          ? firstDataset.axis.label ?? dataset.label
          : dataset.label,
      color: this.hasMultipleDatasets(axisId) ? '#8893aa' : dataset.color,
      font: {
        size: 15,
        family: 'Lato',
      },
    };

    this._chartOptions.scales[axisId]['grid'] = {
      borderDash: [3, 3],
      borderColor: this.hasMultipleDatasets(axisId) ? '#d7dbe6' : dataset.color,
      borderWidth: 2,
      tickWidth: 2,
      tickLength: 5,
      z: -1,
      tickColor: this.hasMultipleDatasets(axisId) ? '#d7dbe6' : dataset.color,
    };

    this.setTicks(dataset);
  }

  /**
   * Sets ticks
   * @param dataset data input
   */
  setTicks(dataset: ChartDataset): void {
    const axisId = dataset.axis ? dataset.axis.id : 'y';

    // set min
    this._chartOptions.scales[axisId]['min'] = dataset.min ?? 0;
    let min = dataset.min;

    // Set max
    let max = dataset.max;

    if (max === undefined) {
      let maxData = Math.max.apply(
        null,
        dataset.data.map((item) => item.value) as number[]
      );
      max = maxData < 1 ? 1 : Math.trunc((maxData + 5) / 5) * 5;
    }

    this._chartOptions.scales[axisId]['suggestedMax'] = max;

    // Set steps
    this._chartOptions.scales[axisId]['ticks']['stepSize'] =
      max / (dataset.nbSteps ?? this._nbDefaultSteps);
    this._chartOptions.scales[axisId]['ticks']['callback'] = (
      value,
      index,
      ticks
    ) => {
      const middle = min + (max - min) / 2;

      let displayedValue = value;
      if (typeof value === 'number') {
        const nbDecimalDigits = dataset.nbDecimalDigits ?? 2;
        const round =
          nbDecimalDigits == 0
            ? Math.pow(10, 1)
            : Math.pow(10, nbDecimalDigits);
        displayedValue = (Math.round(value * round) / round)
          .toFixed(nbDecimalDigits)
          .replace(/\.0+$/, '');
        if (dataset.tickModel !== undefined) {
          if (dataset.tickModel.pattern) {
            displayedValue = String(displayedValue).replace(
              dataset.tickModel.pattern,
              dataset.tickModel.replace
            );
          }
        }

        if (dataset.isMaxLimited && value == max) {
          return '>' + value;
        } else if (dataset.isMinLimited && value == min) {
          return '<' + value;
        }

        return (middle < value && max > value) ||
          (middle > value && min < value)
          ? ''
          : displayedValue;
      } else {
        return value;
      }
    };

    // Set axis x
    this._chartOptions.scales['x']['ticks']['maxTicksLimit'] =
      this.chartXLabels.length;
  }

  /**
   * Toggles hide dataset
   * @param show boolean
   * @param index dataset index
   */
  toggleHideDataset(show: boolean, index: number): void {
    const axisId = this.chartDatasets[index].axis
      ? this.chartDatasets[index].axis.id
      : 'y';

    this.chart.data.datasets[index].hidden = !show;
    this.chartDatasets[index].hidden = !show;

    // Update axis displaying
    this.chart.options.scales[axisId]['display'] =
      this.displayDatasetAxis(axisId);

    this.legend[index]['hidden'] = !show;

    this.chart.update();

    this.toggledFeatureDisplay.emit(this.chartDatasets[index]);
  }

  /**
   * Gets displayed datasets number
   * @returns number
   */
  getDisplayedDatasets(): number {
    return this.legend.filter((item) => !item.hidden).length;
  }

  /**
   * Filters by dataset
   * @param show boolean
   * @param index dataset index
   */
  filterByDatasetFilterOption(show: boolean, index: number): void {
    this.chartDatasetFilter.options[index].show = show;

    this.updateChartDataWithDatasetFilter();

    this.chart.update();

    this.toggledFilterDisplay.emit(this.chartDatasetFilter.options[index]);
  }

  /**
   * Updates chart with dataset filter
   */
  updateChartDataWithDatasetFilter(): void {
    const filterValues: (string | number)[] = this.chartDatasetFilter.options
      .filter((item) => item.show)
      .map((item) => item.value);
    let indexes = [];
    this._chartDatasets = _.cloneDeep(this.chartDatasets);

    this._chartDatasets.forEach((chartDataset) => {
      indexes = [
        ...new Set(
          indexes.concat(
            [...chartDataset.data.keys()].filter(
              (i) =>
                chartDataset.data[i].filteredBy != null &&
                chartDataset.data[i].filteredBy.some((item) =>
                  filterValues.includes(item)
                )
            )
          )
        ),
      ];
    });

    this.chart.data.labels = [];
    this.chart.data.datasets.forEach(
      (dataset, i) => (this.chart.data.datasets[i].data = [])
    );

    if (indexes.length > 0) {
      this.chart.data.labels = this.chartXLabels
        .map((item) => item.title)
        .filter(
          (item, i) => i >= indexes[0] && i <= indexes[indexes.length - 1]
        );

      this._chartDatasets.forEach((chartDataset, i) => {
        this._chartDatasets[i].data = chartDataset.data
          .map((item, j) => {
            item.value = indexes.includes(j) ? item.value : null;
            return item;
          })
          .filter(
            (item, j) => j >= indexes[0] && j <= indexes[indexes.length - 1]
          );
        this.chart.data.datasets[i].data = this._chartDatasets[i].data.map(
          (item) => item.value
        );
      });
    }
  }

  /**
   * Add plugin annotation in chart option 
   * In case phq8 chart only, chart should have color boxes in background 
   */
  setAnnotations(): void {
    this._chartOptions.plugins.annotation.annotations['box1'] = {
      type: 'box',
      scaleID:'y',
      yMin: 0,
      yMax: this.isPhq8 ? 5 : 0,
      backgroundColor: '#d0f8b6',
      borderColor: '#d0f8b6'
    }
    this._chartOptions.plugins.annotation.annotations['box2'] = {
      type: 'box',
      yMin: this.isPhq8 ? 5 : 0,
      yMax: this.isPhq8 ? 9 : 0,
      backgroundColor: '#f4f8b6',
      borderColor: '#f4f8b6'
    }
    this._chartOptions.plugins.annotation.annotations['box3'] = {
      type: 'box',
      yMin: this.isPhq8 ? 9 : 0,
      yMax: this.isPhq8 ? 14 : 0,
      backgroundColor: '#f8e0b6',
      borderColor: '#f8e0b6'
    }
    this._chartOptions.plugins.annotation.annotations['box4'] = {
      type: 'box',
      yMin: this.isPhq8 ? 14 : 0,
      yMax: this.isPhq8 ? 19 : 0,
      backgroundColor: '#f8ccb6',
      borderColor: '#f8ccb6'
    }
    this._chartOptions.plugins.annotation.annotations['box5'] = {
      type: 'box',
      yMin: this.isPhq8 ? 19 : 0,
      yMax: this.isPhq8 ? 24 : 0,
      backgroundColor: '#f8b6b6',
      borderColor: '#f8b6b6'
    }
  }
}
